import axios from "axios";
import { useContext, useEffect, useState, useCallback } from "react";
import { Container, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { CountryContext } from "../context/CountryContext";
import StarRating from "../components/StarRating";
import BackToTop from "../components/BackToTop";

import "../styles/OfferPage.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function OfferPage() {
  const navigate = useNavigate();
  const [exchangeRates, setExchangeRates] = useState({});
  const [offerProducts, setOfferProducts] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [productSizes, setProductSizes] = useState({});
  const [loadingSizes, setLoadingSizes] = useState({});
  const [enableSizes, setEnableSizes] = useState(true);

  const currencySymbols = { US: '$', UK: '£', SL: 'LKR' };
  const { country } = useContext(CountryContext);

  // Fetch company feature settings
  useEffect(() => {
    const fetchFeatureSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/company/get-feature-settings`, {
          params: { company_code: COMPANY_CODE }
        });
        setEnableSizes(response.data.enableSizes !== false);
      } catch (error) {
        console.error('Failed to fetch feature settings:', error);
        // Default to true if API fails
        setEnableSizes(true);
      }
    };
    fetchFeatureSettings();
  }, []);

  const getProductDetails = (styleNumber) => {
    navigate(`/product/${styleNumber}`);
  };

  // Fetch available sizes for a product
  const fetchProductSizes = useCallback(async (styleId) => {
    if (productSizes[styleId]) {
      // Already loaded
      return;
    }

    setLoadingSizes(prev => ({ ...prev, [styleId]: true }));
    try {
      const url = `${BASE_URL}/api/customer/style-variants/${styleId}`;

      const response = await axios.get(url, {
        params: { company_code: COMPANY_CODE }
      });

      if (response.data && Array.isArray(response.data)) {
        // Extract unique sizes from variants
        const sizesSet = new Set();
        response.data.forEach(variant => {
          if (variant.size_name) {
            sizesSet.add(variant.size_name);
          }
        });
        
        const sizesList = Array.from(sizesSet).sort((a, b) => {
          // Sort sizes numerically if they are numbers, otherwise alphabetically
          const aNum = parseInt(a);
          const bNum = parseInt(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.localeCompare(b);
        });

        setProductSizes(prev => ({
          ...prev,
          [styleId]: sizesList
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product sizes:', error);
      // Set empty array on error
      setProductSizes(prev => ({
        ...prev,
        [styleId]: []
      }));
    } finally {
      setLoadingSizes(prev => ({ ...prev, [styleId]: false }));
    }
  }, [productSizes]);

  // fetch items with offer_price
  useEffect(() => {
    const fetchOfferProducts = async () => {
      try {
        setOfferLoading(true);
        setOfferError(null);

        const response = await axios.get(`${BASE_URL}/api/customer/offers`, {
          params: {company_code: COMPANY_CODE}
        });

        // Group products by style_id and select the best offer (minimum offer_price)
        const groupedProducts = {};
        response.data.forEach(product => {
          const styleId = product.style_id;
          
          if (!groupedProducts[styleId]) {
            groupedProducts[styleId] = product;
          } else {
            // If current product has lower offer_price, replace it
            if (product.offer_price < groupedProducts[styleId].offer_price) {
              groupedProducts[styleId] = product;
            }
          }
        });

        // Convert back to array
        const uniqueProducts = Object.values(groupedProducts);
        setOfferProducts(uniqueProducts);
      } catch (error) {
        console.error('Error fetching offer products:', error);
        setOfferError('Failed to load offer products');
      } finally{
        setOfferLoading(false);
      }
    };
    fetchOfferProducts();
  }, []);

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/currency/rates`);
        if (response.data.success) {
          setExchangeRates(response.data.rates);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        // Set default rates if API fails
        setExchangeRates({
          GBP: 0.75,
          LKR: 320
        });
      }
    };
    fetchExchangeRates();
  }, []);

  const getRate = () => {
    switch (country) {
      case 'US':
        return 1;
      case 'UK':
        return exchangeRates['GBP'] || 0.75;
      case 'SL':
        return exchangeRates['LKR'] || 320;
      default:
        return 1;
    }
  };

  const formatPrice = (minPrice) => {
    if (!minPrice) return "Price not defined";
    const symbol = currencySymbols[country] || '$';
    const rate = getRate();
    const convertedPrice = (minPrice * rate).toFixed(2);
    return `${symbol}${convertedPrice}`;
  };

  // Utility function to calculate time left for a given end date
  const calculateTimeLeft = (endDate) => {
    const difference = new Date(endDate) - currentTime;

    if (difference <= 0) {
      return { expired: true };
    }

    return {
      expired: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  // Timer effect for real-time countdown updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Filter to show only active offers (started AND not ended)
  const activeOfferProducts = offerProducts.filter((p) => {
    // Check if offer has valid dates and offer_price
    if (!p.offer_start_date || !p.offer_end_date || Number(p.offer_price) <= 0) {
      return false;
    }
    
    const startDate = new Date(p.offer_start_date);
    const endDate = new Date(p.offer_end_date);
    
    // Offer must have started AND not yet ended
    return startDate <= currentTime && endDate > currentTime;
  });

  return (
      <>
        <Container fluid className="my-5 offer-products-container">
          <h2 className="offer-section-title">
              <i className="fas fa-fire text-danger me-2"></i>
              Hot Deals & Offers 🔥
            </h2>

          {offerLoading ? (
              <div className="text-center my-3">
                <div className="offer-loading-spinner">
                  <div className="spinner-border text-danger" role="status">
                    <span className="visually-hidden">Loading offers...</span>
                  </div>
                  <p className="mt-2">Loading hot deals...</p>
                </div>
              </div>
          ) : offerError ? (
              <div className="text-center my-3">
                <h6 className="text-danger">{offerError}</h6>
              </div>
          ) : activeOfferProducts.length === 0 ? (
              <div className="text-center my-3">
                <i className="fas fa-tag fa-2x text-muted"></i>
                <p className="text-muted mt-2">No special offers available right now.</p>
              </div>
          ) : (
              <div className="offer-products-grid">
                {activeOfferProducts.map((product) => {
                  const timeLeft = calculateTimeLeft(product.offer_end_date);
                  
                  return (
                    <div
                        key={product.style_id}
                        className="offer-product-card"
                        onClick={() => getProductDetails(product.style_number)}
                        onMouseEnter={() => {
                          setHoveredProductId(product.style_id);
                          fetchProductSizes(product.style_id);
                        }}
                        onMouseLeave={() => setHoveredProductId(null)}
                    >
                      <div className="offer-product-image-container">
                        {/* Discount Badge */}
                        <div className="discount-badge">
                          {product.discount_percentage > 0 && (
                              <span className="discount-percentage">
                                -{product.discount_percentage}%
                              </span>
                          )}
                        </div>

                        <img
                            src={`${BASE_URL}/uploads/styles/${product.image}`}
                            alt={product.name}
                            className="offer-product-image"
                        />

                        {/* Size Overlay - appears on hover */}
                        {enableSizes && (
                          <div 
                            className={`offer-product-sizes-overlay ${
                              hoveredProductId === product.style_id ? 'show' : ''
                            }`}
                          >
                            <div className="sizes-content">
                              {/* <p className="sizes-label">Available Sizes:</p> */}
                              {loadingSizes[product.style_id] ? (
                                <div className="text-center py-2">
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </div>
                              ) : (productSizes[product.style_id] && productSizes[product.style_id].length > 0) ? (
                                <div className="sizes-badges">
                                  {productSizes[product.style_id].map((size, idx) => (
                                    <Badge 
                                      key={idx}
                                      bg="light" 
                                      text="dark"
                                    >
                                      {size}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-center text-muted small">No sizes available</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="offer-product-info">
                        <h4 className="offer-product-name">{product.name}</h4>
                        <p className="home-product-description">
                          {product.description && product.description.length > 100
                              ? `${product.description.substring(0, 100)}...`
                              : product.description
                          }
                        </p>

                         {/* Rating Display */}
                        {parseFloat(product.average_rating) > 0 && parseInt(product.review_count) > 0 && (
                          <div className="product-rating mb-2">
                            <StarRating rating={Math.round(parseFloat(product.average_rating))} size="medium" />
                            <span className="rating-text ms-2">
                              {parseFloat(product.average_rating).toFixed(1)}({parseInt(product.review_count)})
                            </span>
                          </div>
                        )}

                        <div className="offer-product-price">
                          <span className="current-price">
                            {formatPrice(product.offer_price)}
                          </span>
                          <span className="original-price">
                            {formatPrice(product.sale_price)}
                          </span>
                        </div>

                        {/* Countdown Timer */}
                        <div className="countdown-timer">
                          {timeLeft.expired ? (
                            <div className="countdown-label text-danger" style={{fontSize: '0.9rem' }}>Offer Ended</div>
                          ) : (
                            <>
                              <div className="countdown-display text-danger" style={{fontSize: '0.9rem' }}>
                                {timeLeft.days > 0 && (
                                  <span className="time-unit">
                                    <span className="time-value">{timeLeft.days}</span>
                                    <span className="time-label">d </span>
                                  </span>
                                )}
                                <span className="time-unit">
                                  <span className="time-value">
                                    {timeLeft.hours.toString().padStart(2, "0")}
                                  </span>
                                  <span className="time-label">h </span>
                                </span>
                                <span className="time-unit">
                                  <span className="time-value">
                                    {timeLeft.minutes.toString().padStart(2, "0")}
                                  </span>
                                  <span className="time-label">m </span>
                                </span>
                                <span className="time-unit">
                                  <span className="time-value">
                                    {timeLeft.seconds.toString().padStart(2, "0")}
                                  </span>
                                  <span className="time-label">s </span>
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* {product.category_name && (
                            <div className="offer-product-category">
                              <span className="offer-category-badge">
                                {product.category_name}
                              </span>
                            </div>
                        )} */}
                      </div>
                    </div>
                  );
                })}
              </div>
          )}
        </Container>

        <BackToTop />
     </>
  );
}